import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, CircleHelp, Loader2 } from 'lucide-react';
import type { DisplayStatus } from '../../../../helper/PaymentUtils';
import { formatCurrency, formatDate, formatTime, mapApiStatus } from '../../../../helper/PaymentUtils';
import type { EnrichedPayment } from '../../../../types/user.types';
import { ActionButton } from './ActionButton/ActionButton';
import { DesktopTableRow } from './DesktopTableRow/DesktopTableRow';
import { StatusBadge } from './StatusBadge/StatusBadge';

export interface ProcessedPayment extends EnrichedPayment {
  displayStatus: DisplayStatus;
  displayAmount: number;
  displayStartedAt: string;
}

type SortKey = 'product' | 'status' | 'amount' | 'startedAt';
type SortDirection = 'ascending' | 'descending';

interface SortConfig {
  key: SortKey | null;
  direction: SortDirection;
}

interface PaymentsTableProps {
  payments: EnrichedPayment[] | null | undefined;
  pollingPaymentId?: number | null;
}

export const PaymentsTable: React.FC<PaymentsTableProps> = ({ payments, pollingPaymentId }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'status', direction: 'ascending' });

  const processedPayments: ProcessedPayment[] = useMemo(() => {
    const validPayments = Array.isArray(payments) ? payments : [];

    return validPayments.map((p) => ({
      ...p,
      displayStatus: mapApiStatus(p.paymentStatus),
      displayAmount: (p.billedAmount ?? 0) / 100,
      displayStartedAt: p.paymentStartedAt ?? '',
    }));
  }, [payments]);

  const sortedPayments = useMemo(() => {
    const sortableItems = [...processedPayments];
    if (sortConfig.key !== null) {
      const statusPriority: Record<DisplayStatus, number> = {
        Unpaid: 0,
        Started: 1,
        Paid: 2,
        Refunded: 3,
        Expired: 4,
        Unknown: 5,
      };
      sortableItems.sort((a, b) => {
        let comparison = 0;
        const key = sortConfig.key!;
        if (key === 'status') {
          comparison = statusPriority[a.displayStatus] - statusPriority[b.displayStatus];
        } else if (key === 'startedAt') {
          comparison = new Date(a.displayStartedAt).getTime() - new Date(b.displayStartedAt).getTime();
        } else if (key === 'amount') {
          comparison = a.displayAmount - b.displayAmount;
        } else if (key === 'product') {
          comparison = (a.productLabel || '').localeCompare(b.productLabel || '', undefined, { sensitivity: 'base' });
          if (comparison === 0 && a.appointment && b.appointment) {
            comparison = new Date(a.appointment.startDate).getTime() - new Date(b.appointment.startDate).getTime();
          } else if (comparison === 0 && a.appointment) {
            comparison = -1;
          } else if (comparison === 0 && b.appointment) {
            comparison = 1;
          }
        }

        return sortConfig.direction === 'ascending' ? comparison : comparison * -1;
      });
    }

    return sortableItems;
  }, [processedPayments, sortConfig]);

  const requestSort = (key: SortKey) => {
    let direction: SortDirection = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const MobileAccordionItem: React.FC<{ payment: ProcessedPayment; isPolling: boolean }> = ({ payment, isPolling }) => {
    const openByDefault = payment.displayStatus === 'Unpaid' || payment.displayStatus === 'Started';
    const needsAction = payment.displayStatus === 'Unpaid' && !isPolling;
    const [isOpen, setIsOpen] = useState<boolean>(openByDefault || isPolling);
    const toggleOpen = () => setIsOpen(!isOpen);

    const bgColor = isPolling
      ? 'bg-blue-50 border-blue-200'
      : needsAction
        ? 'bg-orange-50 border-orange-200'
        : 'bg-white border-gray-200';
    const headerTextColor = isPolling ? 'text-blue-900' : needsAction ? 'text-orange-900' : 'text-gray-900';
    const amountColor = isPolling ? 'text-blue-800' : needsAction ? 'text-orange-800' : 'text-gray-800';
    const iconColor = isPolling ? 'text-blue-700' : needsAction ? 'text-orange-700' : 'text-gray-500';

    const appointment = payment.appointment;

    return (
      <div className={`shadow-md rounded-lg border ${bgColor} overflow-hidden transition-colors duration-150`}>
        <button
          type='button'
          tabIndex={0}
          onClick={toggleOpen}
          className={`w-full flex justify-between items-center p-4 text-left ${isOpen ? 'border-b border-gray-200' : ''} hover:bg-gray-50 transition-colors duration-150`}
          aria-expanded={isOpen}
          aria-controls={`payment-details-${payment.id}`}
        >
          <div className='flex-grow mr-2 overflow-hidden'>
            <span className={`block text-sm font-semibold ${headerTextColor} break-words`}>
              {payment.productLabel || `ID: ${payment.id}`}
            </span>
          </div>
          <div className='flex items-center flex-shrink-0 ml-auto pl-2'>
            <span className={`text-sm font-medium ${amountColor} mr-3`}>{formatCurrency(payment.displayAmount)}</span>
            {isOpen ? (
              <ChevronUp className={`h-5 w-5 ${iconColor}`} />
            ) : (
              <ChevronDown className={`h-5 w-5 ${iconColor}`} />
            )}
          </div>
        </button>
        {isOpen && (
          <div id={`payment-details-${payment.id}`} className='p-4 space-y-3 bg-white lg:bg-transparent'>
            <div className='flex justify-between items-center'>
              <span className='text-xs font-medium text-gray-500'>Zahlungsdatum:</span>
              <span className='text-sm text-gray-700'>{formatDate(payment.displayStartedAt)}</span>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-xs font-medium text-gray-500'>Status:</span>
              {isPolling ? (
                <div className='flex items-center gap-2 text-sm text-blue-800'>
                  <Loader2 className='w-4 h-4 animate-spin' />
                  <span>Wird verarbeitet...</span>
                </div>
              ) : (
                <StatusBadge status={payment.displayStatus} />
              )}
            </div>

            {appointment && (
              <div className='flex justify-between items-start pt-3 border-t border-gray-100 mt-3'>
                <span className='text-xs font-medium text-gray-500 pt-0.5'>Termin:</span>
                <div className='text-sm text-gray-700 text-right'>
                  <div>{`${appointment.doctorTitle || ''} ${appointment.doctorName}`.trim()}</div>
                  <div>
                    {formatDate(appointment.startDate)}
                    {` (${formatTime(appointment.startDate)} - ${formatTime(appointment.endDate)})`}
                  </div>
                </div>
              </div>
            )}

            <div className='flex justify-end pt-2'>
              {isPolling ? <div className='h-9 w-24' /> : <ActionButton payment={payment} />}
            </div>
          </div>
        )}
      </div>
    );
  };

  const SortableHeader: React.FC<{ columnKey: SortKey; title: string; tooltip?: string; className?: string }> = ({
    columnKey,
    title,
    tooltip,
    className = '',
  }) => {
    const isSorted = sortConfig.key === columnKey;
    const SortIcon = sortConfig.direction === 'ascending' ? ArrowUp : ArrowDown;

    return (
      <th
        scope='col'
        className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors duration-150 group relative ${className}`}
        onClick={() => requestSort(columnKey)}
        title={`Nach ${title} sortieren`}
        aria-sort={isSorted ? sortConfig.direction : 'none'}
      >
        <div
          className='flex items-center'
          tabIndex={0}
          role='button'
          onKeyDown={(e) => e.key === 'Enter' && requestSort(columnKey)}
        >
          <span>{title}</span>
          {tooltip && (
            <span className='ml-1.5 relative' title={tooltip}>
              <CircleHelp className='h-3.5 w-3.5 text-gray-400 hover:text-gray-600 peer' />
            </span>
          )}
          {isSorted && <SortIcon className='w-3 h-3 ml-1.5 flex-shrink-0 text-gray-600' aria-hidden='true' />}
        </div>
      </th>
    );
  };

  const NoPaymentsMessage = () => (
    <div className='text-center p-6 text-gray-500 bg-white rounded-lg border border-gray-200 shadow-md mt-4 md:mt-0 md:rounded-b-lg md:border-t'>
      Keine Zahlungsdatensätze gefunden.
    </div>
  );

  const DesktopTable = () => (
    <div className='hidden lg:block overflow-x-auto shadow-md rounded-lg border border-gray-200'>
      <table className='min-w-full divide-y divide-gray-200'>
        <thead className='bg-gray-50'>
          <tr>
            <SortableHeader columnKey='product' title='Leistung' className='min-w-[250px]' />
            <SortableHeader columnKey='status' title='Status' />
            <SortableHeader
              columnKey='startedAt'
              title='Zahlungsdatum'
              tooltip='Dies ist das Datum der Zahlung und nicht notwendigerweise das Datum der Leistungserbringung'
            />
            <SortableHeader columnKey='amount' title='Betrag' />
            <th scope='col' className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
              Aktionen
            </th>
          </tr>
        </thead>
        <tbody className='bg-white divide-y divide-gray-200'>
          {sortedPayments.map((payment) => (
            <DesktopTableRow
              key={payment.id.toString()}
              payment={payment}
              isPolling={payment.id === pollingPaymentId} // Pass the prop here
            />
          ))}
        </tbody>
      </table>
    </div>
  );

  const MobileAccordions = () => (
    <div className='block lg:hidden space-y-3'>
      {sortedPayments.map((payment) => (
        <MobileAccordionItem
          key={payment.id.toString()}
          payment={payment}
          isPolling={payment.id === pollingPaymentId}
        />
      ))}
    </div>
  );

  return (
    <div>
      {sortedPayments.length === 0 ? (
        <NoPaymentsMessage />
      ) : (
        <>
          <DesktopTable />
          <MobileAccordions />
        </>
      )}
    </div>
  );
};
